import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, User } from 'lucide-react';
import { SLOT_LABEL, STATUS_LABEL, formatDay } from '@nu/shared';
import { LEAVE_LABEL, SLOT_SHORT, addDays, chipTitle, fillLevel, shortName, slotsText, slotsUsed } from './shared.jsx';

// Week planner: a row per team (plus bookings not on a team yet), a column
// per day. Each cell shows that team's bookings for the day against its
// daily target. Click a booking to open it, click + to book into that team
// and day, or drag a booking to another team or day. Team members on
// holiday (from BrightHR) are shown in the day they're off.

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOT_ORDER = { am: 0, all_day: 1, pm: 2 };


// waiting: revisits with no date yet (dragged in from above the calendar).
export default function BookingsWeek({ weekStart, today, bookings, waiting = [], teams, people = [], leave = [], colours = new Map(), canEdit, onOpen, onNew, onMove }) {
  const days = DAY_NAMES.map((name, i) => ({ name, date: addDays(weekStart, i) }));
  const [dragging, setDragging] = useState(null); // booking id
  const [over, setOver] = useState(null); // `${teamKey}|${date}`

  const rows = [
    ...teams.filter((t) => t.active || bookings.some((b) => b.teamId === t.id)).map((t) => ({ key: String(t.id), teamId: t.id, name: t.name, target: t.target?.value ?? null, closed: !t.active, members: t.members || [] })),
    { key: 'none', teamId: null, name: 'Not on a team', target: null },
  ];
  const cell = (teamId, date) => bookings
    .filter((b) => (b.teamId ?? null) === teamId && b.bookingDate === date)
    .sort((a, b) => (SLOT_ORDER[a.slot] ?? 3) - (SLOT_ORDER[b.slot] ?? 3) || a.id - b.id);

  const nameOf = new Map(people.map((p) => [p.id, p.name]));
  // Members of the team on that day who are on leave.
  const off = (row, date) => (row.members || [])
    .filter((m) => m.fromDate <= date && (!m.toDate || m.toDate >= date))
    .map((m) => ({ m, l: leave.find((x) => x.personId === m.personId && x.day === date) }))
    .filter((x) => x.l)
    .map(({ m, l }) => ({ id: m.personId, name: nameOf.get(m.personId) || 'Someone', part: l.part }));

  const drop = (row, date) => (e) => {
    e.preventDefault();
    setOver(null);
    const id = Number(e.dataTransfer.getData('text/plain'));
    const b = bookings.find((x) => x.id === id) || waiting.find((x) => x.id === id);
    setDragging(null);
    if (!b || ((b.teamId ?? null) === row.teamId && b.bookingDate === date)) return;
    onMove(b, row.teamId, date);
  };

  return (
    <div className="table-scroll">
      <table className="cal">
        <thead>
          <tr>
            <th className="cal-team">Team</th>
            {days.map((d) => (
              <th key={d.date} className={d.date === today ? 'cal-today' : undefined}>
                <Link to={`/plan-the-day?day=${d.date}`} className="cal-day-link" title={`Plan ${formatDay(d.date)}`}>{d.name} <span className="muted">{formatDay(d.date).slice(0, 5)}</span></Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.teamId ? undefined : 'cal-unassigned'} style={row.teamId ? { '--team': colours.get(row.teamId) } : undefined}>
              <th className="cal-team" scope="row">
                <strong>{row.name}</strong>{row.closed && <span className="tag">Closed</span>}
                {row.members?.length > 0 && (
                  <ul className="crew">
                    {row.members.map((m) => (
                      <li key={m.personId}><User size={13} aria-hidden="true" />{shortName(people.find((p) => p.id === m.personId)?.name)}</li>
                    ))}
                  </ul>
                )}
              </th>
              {days.map((d) => {
                const list = cell(row.teamId, d.date);
                const used = slotsUsed(list);
                const key = `${row.key}|${d.date}`;
                const full = row.target !== null && used >= row.target;
                const fill = fillLevel(used, row.target);
                return (
                  <td
                    key={d.date}
                    className={`cal-cell${d.date === today ? ' cal-today' : ''}${over === key ? ' cal-over' : ''}${fill ? ` fill-${fill}` : ''}`}
                    onDragOver={canEdit ? (e) => { e.preventDefault(); setOver(key); } : undefined}
                    onDragLeave={canEdit ? () => setOver((o) => (o === key ? null : o)) : undefined}
                    onDrop={canEdit ? drop(row, d.date) : undefined}
                  >
                    {row.target !== null && (list.length > 0 || canEdit) && (
                      <div className={`cal-load${full ? (used > row.target ? ' over' : ' full') : ''}`}>{slotsText(used)} / {row.target}</div>
                    )}
                    {off(row, d.date).map((o) => (
                      <div key={o.id} className={`cal-leave leave-${o.part}`} title={`${o.name} is on holiday${o.part === 'all' ? '' : ` (${o.part.toUpperCase()})`}`}>
                        {shortName(o.name)} {LEAVE_LABEL[o.part]}
                      </div>
                    ))}
                    {list.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        className={`cal-chip status-${b.status}${b.revisitOf ? ' cal-revisit' : ''}`}
                        draggable={canEdit}
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(b.id)); e.dataTransfer.effectAllowed = 'move'; setDragging(b.id); }}
                        onDragEnd={() => { setDragging(null); setOver(null); }}
                        onClick={() => onOpen(b.id)}
                        title={chipTitle(b)}
                      >
                        <span className="chip-slot">{SLOT_SHORT[b.slot] || '–'}</span> {b.reference}{b.revisitOf && <span className="chip-rv" aria-label="Revisit">RV</span>}
                      </button>
                    ))}
                    {canEdit && (
                      <button type="button" className="cal-add" onClick={() => onNew({ teamId: row.teamId, bookingDate: d.date })} aria-label={`Book ${row.name} on ${formatDay(d.date)}`}>
                        <Plus size={16} aria-hidden="true" />
                      </button>
                    )}
                    {fill && <div className="cal-pct">{Math.round((used / row.target) * 100)}% booked</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
